import {
  AddIcon,
  Button,
  DeleteIcon,
  FormField,
  Input,
  ModalDialog,
  Numeric,
  Select,
  Switch,
} from "@rothoblaas/design-system";
import { FC, useContext, useState } from "react";
import Icon from "../../../../components/ui/Icon";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import type { ElementType } from "../../../../http/envelopeDesigner/types/calculation";
import type {
  ExampleAssembly,
  ExampleAssemblyLayer,
  ExampleAssemblyRegion,
  ExampleAssemblyStructure,
} from "../../../../http/envelopeDesigner/types/exampleAssembly";
import type { Material } from "../../../../http/envelopeDesigner/types/material";
import {
  ExampleAssemblyBody,
  useAdminEnvelopeDesignerApi,
} from "../../../../http/envelopeDesigner/useAdminEnvelopeDesigner";
import styles from "./AdminForm.module.css";

const ELEMENTS: ElementType[] = ["wall", "roof", "ground"];
const STRUCTURES: ExampleAssemblyStructure[] = ["timber", "mass_timber", "masonry", "concrete"];
const REGIONS: ExampleAssemblyRegion[] = ["EU", "NA"];

type Option<T> = { value: T; label: string };
/** The translated fields (suffix) and their language; null = the English text. */
const TRANSLATIONS = [
  ["It", "IT"],
  ["De", "DE"],
  ["Fr", "FR"],
  ["Es", "ES"],
] as const;

const emptyBody: ExampleAssemblyBody = {
  code: "",
  label: "",
  labelIt: null,
  labelDe: null,
  labelFr: null,
  labelEs: null,
  category: "wall",
  structure: "timber",
  regions: ["EU"],
  description: null,
  descriptionIt: null,
  descriptionDe: null,
  descriptionFr: null,
  descriptionEs: null,
  isDefault: false,
  layers: [],
};

// Edit or create an example stratigraphy: metadata plus its layers inside -> outside, each with its
// optional bridges (studs/rafters; spacing is the CLEAR gap, empty depth = the whole layer).
const ExampleFormDialog: FC<{ example: ExampleAssembly | null; onClose: () => void }> = ({ example, onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.envelopeDesignerModule.admin;
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminEnvelopeDesignerApi();
  const { data: materials = [] } = adminApi.useAllAdminMaterials();
  const [body, setBody] = useState<ExampleAssemblyBody>(() => {
    if (!example) return emptyBody;
    const { id: _id, visibility: _v, order: _o, ...rest } = example;
    return rest;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const update = (patch: Partial<ExampleAssemblyBody>) => setBody((current) => ({ ...current, ...patch }));
  const updateLayer = (index: number, patch: Partial<ExampleAssemblyLayer>) =>
    update({ layers: body.layers.map((layer, i) => (i === index ? { ...layer, ...patch } : layer)) });

  const materialOptions: Option<string>[] = materials.map((material: Material) => ({
    value: material.id,
    label: material.manufacturer ? `${material.name} (${material.manufacturer})` : material.name,
  }));
  const materialSelect = (value: string, onChange: (id: string) => void) => (
    <Select<Option<string>, false>
      value={materialOptions.find((option) => option.value === value) ?? null}
      options={materialOptions}
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.label}
      onChange={(selected) => selected && onChange(selected.value)}
    />
  );
  const select = <T extends string>(label: string, value: T, values: readonly T[], onChange: (value: T) => void) => {
    const options: Option<T>[] = values.map((v) => ({ value: v, label: v }));
    return (
      <FormField label={label}>
        {({ labelId }) => (
          <Select<Option<T>, false>
            inputId={labelId}
            value={options.find((option) => option.value === value) ?? null}
            options={options}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            onChange={(selected) => selected && onChange(selected.value)}
          />
        )}
      </FormField>
    );
  };

  const save = async () => {
    setIsSubmitting(true);
    try {
      await adminApi.saveExampleAssembly(example?.id ?? null, body);
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        example ? `${i18n.translation.common.edit} ${example.label}` : `${i18n.translation.common.add} ${t.example}`
      }
      className={styles.modal}
      isSubmitting={isSubmitting}
      buttons={[
        <Button key="cancel" variant="secondary" onClick={onClose}>
          {i18n.translation.common.cancel}
        </Button>,
        <Button key="save" onClick={save}>
          {i18n.translation.common.save}
        </Button>,
      ]}
    >
      <div className={styles.grid}>
        <FormField label={t.label}>
          {({ labelId }) => (
            <Input id={labelId} value={body.label} onChange={(event) => update({ label: event.target.value })} />
          )}
        </FormField>
        <FormField label={i18n.translation.common.code}>
          {({ labelId }) => (
            <Input id={labelId} value={body.code} onChange={(event) => update({ code: event.target.value })} />
          )}
        </FormField>
        {select(t.element, body.category, ELEMENTS, (category) => update({ category }))}
        {select(t.structure, body.structure, STRUCTURES, (structure) => update({ structure }))}
        <div className={styles.checks}>
          {t.regions}:
          {REGIONS.map((region) => (
            <label key={region}>
              <input
                type="checkbox"
                checked={body.regions.includes(region)}
                onChange={(event) =>
                  update({
                    regions: event.target.checked
                      ? [...body.regions, region]
                      : body.regions.filter((other) => other !== region),
                  })
                }
              />{" "}
              {region}
            </label>
          ))}
        </div>
        <FormField label={t.default}>
          {({ labelId }) => (
            <Switch id={labelId} checked={body.isDefault} onChange={(isDefault) => update({ isDefault })} />
          )}
        </FormField>
        {TRANSLATIONS.map(([suffix, language]) => (
          <FormField key={language} label={`${t.label} (${language})`} isOptional>
            {({ labelId }) => (
              <Input
                id={labelId}
                value={body[`label${suffix}`] ?? ""}
                onChange={(event) => update({ [`label${suffix}`]: event.target.value || null })}
              />
            )}
          </FormField>
        ))}
        {(["", ...TRANSLATIONS.map(([suffix]) => suffix)] as const).map((suffix) => (
          <div key={suffix} className={styles.wide}>
            <FormField label={suffix ? `${t.description} (${suffix.toUpperCase()})` : t.description} isOptional>
              {({ labelId }) => (
                <textarea
                  id={labelId}
                  className={styles.textarea}
                  value={body[`description${suffix}`] ?? ""}
                  onChange={(event) => update({ [`description${suffix}`]: event.target.value || null })}
                />
              )}
            </FormField>
          </div>
        ))}

        <div className={`${styles.wide} ${styles.layers}`}>
          <b>{t.layersInsideOut}</b>
          {body.layers.map((layer, index) => (
            <div key={index}>
              <div className={styles.layerRow}>
                <span>{index + 1}.</span>
                {materialSelect(layer.materialId, (materialId) => updateLayer(index, { materialId }))}
                <Numeric
                  value={layer.thicknessMm}
                  suffix=" mm"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={(value) => updateLayer(index, { thicknessMm: value ?? 0 })}
                />
                <button
                  type="button"
                  className={styles.iconButton}
                  title={t.addBridge}
                  onClick={() =>
                    updateLayer(index, {
                      bridges: [
                        ...layer.bridges,
                        { materialId: layer.materialId, widthMm: 60, spacingMm: 565, depthMm: null },
                      ],
                    })
                  }
                >
                  <Icon glyph={AddIcon} />
                </button>
                <button
                  type="button"
                  className={styles.iconButton}
                  title={i18n.translation.common.delete}
                  onClick={() => update({ layers: body.layers.filter((_, i) => i !== index) })}
                >
                  <Icon glyph={DeleteIcon} />
                </button>
              </div>
              {layer.bridges.map((bridge, bridgeIndex) => {
                const setBridge = (patch: Partial<typeof bridge>) =>
                  updateLayer(index, {
                    bridges: layer.bridges.map((other, j) => (j === bridgeIndex ? { ...other, ...patch } : other)),
                  });
                return (
                  <div key={bridgeIndex} className={styles.bridgeRow}>
                    <span>↳</span>
                    {materialSelect(bridge.materialId, (materialId) => setBridge({ materialId }))}
                    <Numeric
                      value={bridge.widthMm}
                      suffix=" mm"
                      decimalScale={1}
                      allowNegative={false}
                      placeholder={t.width}
                      onValueChange={(value) => setBridge({ widthMm: value ?? 0 })}
                    />
                    <Numeric
                      value={bridge.spacingMm}
                      suffix=" mm"
                      decimalScale={1}
                      allowNegative={false}
                      placeholder={t.spacing}
                      onValueChange={(value) => setBridge({ spacingMm: value ?? 0 })}
                    />
                    <Numeric
                      value={bridge.depthMm}
                      suffix=" mm"
                      decimalScale={1}
                      allowNegative={false}
                      placeholder={t.fullDepth}
                      onValueChange={(value) => setBridge({ depthMm: value })}
                    />
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={() => updateLayer(index, { bridges: layer.bridges.filter((_, j) => j !== bridgeIndex) })}
                    >
                      <Icon glyph={DeleteIcon} />
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
          <div>
            <Button
              variant="secondary"
              onClick={() =>
                update({
                  layers: [...body.layers, { materialId: materials[0]?.id ?? "", thicknessMm: 50, bridges: [] }],
                })
              }
            >
              <Icon glyph={AddIcon} /> {t.addLayer}
            </Button>
          </div>
        </div>
      </div>
    </ModalDialog>
  );
};

export default ExampleFormDialog;
